import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../axios";
import { Sparkles, ArrowRight, Tag, BookOpen, Percent } from "lucide-react";
import { getS3URL } from "../../_helpers";
import "./OffersSection.scss";

const OffersSection = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await axiosInstance.get("/offers");
        if (res?.data && !res.data.error) {
          setOffers(res.data.data);
        }
      } catch (err) {
        console.error("Error loading offers on homepage:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  if (loading || offers.length === 0) {
    return null; // Don't render anything if loading or no offers exist
  }

  return (
    <section className="homepage-offers-section">
      <div className="section-container">
        <div className="section-header-wrap">
          <div className="flex items-center space-x-2">
            <div className="promo-badge">
              <Percent size={14} className="text-white" />
              <span>SPECIAL BUNDLES</span>
            </div>
            <h2 className="section-title">Limited Time Course Offers</h2>
          </div>
          <p className="section-subtitle">
            Maximize your learning and save big. Combine popular courses into value packages.
          </p>
        </div>

        <div className="offers-cards-grid">
          {offers.map((offer) => {
            const originalPrice = parseFloat(offer.totalAmount) || 0;
            const savings = parseFloat(offer.discount) || 0;
            const discountedPrice = parseFloat(offer.discountedAmount) || 0;

            return (
              <div 
                key={offer.id} 
                className="homepage-offer-card"
                onClick={() => navigate("/special-offers")}
              >
                <div className="card-gradient-overlay"></div>
                <div className="card-content-wrap">
                  <div className="card-top-header">
                    <span className="category-tag">
                      <Tag size={12} className="mr-1" />
                      {offer.category}
                    </span>
                    <span className="savings-badge">
                      SAVE ₹{savings.toFixed(0)}
                    </span>
                  </div>

                  <h3 className="offer-card-title">{offer.title}</h3>
                  <p className="offer-card-description">{offer.description}</p>

                  <div className="included-courses-preview">
                    <div className="flex items-center text-xs text-indigo-600 font-bold mb-2">
                      <BookOpen size={12} className="mr-1 text-indigo-500" />
                      <span>INCLUDES {offer.coursesData?.length || 0} COURSES:</span>
                    </div>

                    {/* Courses Image Pile / Stack */}
                    <div className="courses-avatar-stack flex items-center mb-3">
                      {offer.coursesData?.slice(0, 4).map((c, idx) => {
                        const courseImg = c?.imageFiles?.[0]?.fileName 
                          ? getS3URL(c.imageFiles[0].fileName)
                          : "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg";
                        return (
                          <img 
                            key={c.ID}
                            src={courseImg} 
                            alt={c.CourseName} 
                            className="course-stack-avatar" 
                            title={c.CourseName}
                            style={{
                              zIndex: 10 - idx,
                              marginLeft: idx > 0 ? "-12px" : "10px"
                            }}
                          />
                        );
                      })}
                      {offer.coursesData?.length > 4 && (
                        <div 
                          className="course-stack-avatar-more"
                          style={{
                            zIndex: 1,
                            marginLeft: "-10px"
                          }}
                        >
                          +{offer.coursesData.length - 4}
                        </div>
                      )}
                    </div>

                    <ul className="courses-bullet-list">
                      {offer.coursesData?.slice(0, 3).map((c) => (
                        <li key={c.ID} className="course-bullet-item truncate">
                          {c.CourseName}
                        </li>
                      ))}
                      {offer.coursesData?.length > 3 && (
                        <li className="course-bullet-item text-indigo-600 font-semibold">
                          + {offer.coursesData.length - 3} more courses...
                        </li>
                      )}
                    </ul>
                  </div>

                  <div className="card-pricing-footer">
                    <div className="price-label-group">
                      <span className="original-price">₹{originalPrice.toFixed(2)}</span>
                      <span className="discounted-price">₹{discountedPrice.toFixed(2)}</span>
                    </div>
                    <button className="btn-claim-deal">
                      <span>View Deal</span>
                      <ArrowRight size={14} className="btn-arrow" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default OffersSection;
